import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./api";

export type PushStatus = {
  configured: boolean;
  subscriptions: number;
};

export type PushSupportState = "supported" | "unsupported" | "insecure-context";

export const pushKeys = {
  status: ["push", "status"] as const,
  subscription: ["push", "subscription"] as const,
};

export function detectPushSupport(): PushSupportState {
  if (typeof window === "undefined") return "unsupported";
  if (!window.isSecureContext) return "insecure-context";
  if (!("serviceWorker" in navigator)) return "unsupported";
  if (!("PushManager" in window)) return "unsupported";
  if (!("Notification" in window)) return "unsupported";
  return "supported";
}

export function usePushStatus() {
  return useQuery({
    queryKey: pushKeys.status,
    queryFn: () => apiFetch<PushStatus>("/push/status"),
  });
}

/** True = browser has an active subscription for this origin. */
export function useLocalPushSubscription() {
  return useQuery({
    queryKey: pushKeys.subscription,
    queryFn: async () => {
      if (detectPushSupport() !== "supported") return null;
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return null;
      const sub = await reg.pushManager.getSubscription();
      return sub?.toJSON() ?? null;
    },
    staleTime: 10_000,
  });
}

export function useEnablePush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (detectPushSupport() !== "supported") {
        throw new Error("Push notifications aren't supported in this browser.");
      }

      const { publicKey, configured } = await apiFetch<{
        publicKey: string | null;
        configured: boolean;
      }>("/push/public-key");
      if (!configured || !publicKey) {
        throw new Error("Push isn't configured on the server.");
      }

      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error(
          permission === "denied"
            ? "Permission denied. Enable notifications in your browser settings."
            : "Permission not granted.",
        );
      }

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToBuffer(publicKey),
      });

      await apiFetch("/push/subscribe", {
        method: "POST",
        body: JSON.stringify(sub.toJSON()),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["push"] });
    },
  });
}

export function useDisablePush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (detectPushSupport() !== "supported") return;
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return;
      const endpoint = sub.endpoint;
      await sub.unsubscribe().catch(() => undefined);
      await apiFetch("/push/subscribe", {
        method: "DELETE",
        body: JSON.stringify({ endpoint }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["push"] });
    },
  });
}

export type PushTestResult = {
  attempted: number;
  delivered: number;
  deadCleaned: number;
};

/**
 * Fires a test push to every subscription for the current user — bypasses
 * `dueCount` and reminder-hour checks. Used by the "Send test now" button
 * in Settings to verify the whole pipeline when daily reminders aren't
 * arriving. Returns per-subscription result so the UI can report what
 * actually happened (subscribed but nothing delivered ⇒ VAPID mismatch
 * or revoked endpoint).
 */
export function useSendTestPush() {
  return useMutation({
    mutationFn: () => apiFetch<PushTestResult>("/push/test", { method: "POST" }),
  });
}

function urlBase64ToBuffer(base64String: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return buffer;
}
